import React, { useState, useEffect } from "react";
import {
  Search,
  MoreVertical,
  Building,
  Mail,
  Phone
} from "lucide-react";
import { ClientService } from "../../services/clients/clientService";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface Client {
  id: string;
  raison_sociale: string;
  email: string;
  telephone: string;
  siret: string;
  statut: 'actif' | 'en_attente' | 'suspendu' | 'inactif';
  is_active: boolean; // Keep for backward compatibility if needed, but rely on statut
  user: {
      email: string;
      first_name: string;
      last_name: string;
  };
  created_at: string;
}

export const AdminClients: React.FC = () => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [updating, setUpdating] = useState(false);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await ClientService.getClients();
      setClients(response.data.results || []);
    } catch (error) {
      console.error("Erreur chargement clients", error);
      toast.error(t('clients.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id: string, newStatus: string) => {
      if (updating) return;
      
      // Optimistic update
      const previousClients = [...clients];
      setClients(clients.map(c => c.id === id ? { ...c, statut: newStatus as any } : c));
      
      try {
          setUpdating(true);
          await ClientService.updateClient(id, { statut: newStatus });
          toast.success(t('clients.messages.statusUpdated', { status: newStatus }));
          fetchClients(); // Refresh to ensure sync
      } catch (error) {
          console.error("Erreur update status", error);
          toast.error(t('clients.messages.updateError'));
          setClients(previousClients); // Revert
      } finally {
          setUpdating(false);
      }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const filteredClients = clients.filter(
    (client) =>
      client.raison_sociale.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.siret.includes(searchTerm)
  );

  if (loading && clients.length === 0) {
      return (
          <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
      );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('clients.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('clients.overview')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          {/* Add Client Button could go here */}
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder={t('clients.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredClients.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    {t('common.noResult')}
                </div>
          ) : (
          filteredClients.map((client) => (
            <div key={client.id} className="p-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                      <Building className="h-6 w-6 text-primary-600" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center">
                      <h4 className="text-sm font-medium text-gray-900">
                        {client.raison_sociale}
                      </h4>
                      <span
                        className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          client.statut === 'actif'
                            ? "bg-success-100 text-success-800"
                            : client.statut === 'en_attente'
                            ? "bg-warning-100 text-warning-800"
                            : "bg-error-100 text-error-800"
                        }`}
                      >
                        {client.statut === 'actif' ? t('clients.status.active') : client.statut === 'en_attente' ? t('clients.status.pending') : client.statut === 'suspendu' ? t('clients.status.suspended') : t('clients.status.inactive')}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center text-sm text-gray-500">
                      <Phone className="h-4 w-4 mr-1" />
                      <span className="mr-4">{client.telephone}</span>
                      <Mail className="h-4 w-4 mr-1" />
                      <span className="text-gray-400">{client.email}</span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                      SIRET: {client.siret}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {client.statut === 'en_attente' && (
                    <button
                      onClick={() => handleUpdateStatus(client.id, 'actif')}
                      className="p-1 px-3 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs font-medium"
                    >
                      {t('clients.actions.approve')}
                    </button>
                  )}
                  {client.statut === 'actif' && (
                    <button
                      onClick={() => handleUpdateStatus(client.id, 'suspendu')}
                      className="p-1 px-3 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-xs font-medium"
                    >
                      {t('clients.actions.suspend')}
                    </button>
                  )}
                  {(client.statut === 'suspendu' || client.statut === 'inactif') && (
                    <button
                      onClick={() => handleUpdateStatus(client.id, 'actif')}
                      className="p-1 px-3 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs font-medium"
                    >
                      {t('clients.actions.reactivate')}
                    </button>
                  )}
                  <button className="p-2 text-gray-400 hover:text-gray-500">
                    <MoreVertical className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
          )}
        </div>
      </div>
    </div>
  );
};
